/** Catálogo de roles CMS (plantillas de módulos y sistemas). */

import { CMS_MODULE_IDS, ROLE_MODULE_TEMPLATES, sanitizeModules } from "./cmsAccess";
import { ALL_PLATFORM_SYSTEM_IDS } from "./platformSystems";

export const CMS_ROLES_DOC = { collection: "site", id: "cmsRoles" };

export const BUILTIN_ROLE_IDS = ["owner", "admin", "editor", "vendedor", "calidad", "medico"];

export function defaultCmsRoles() {
  return [
    {
      id: "owner",
      label: "owner (acceso total)",
      builtin: true,
      staffOtp: true,
      canManageUsers: true,
      modules: [...CMS_MODULE_IDS],
      systems: [...ALL_PLATFORM_SYSTEM_IDS],
    },
    {
      id: "admin",
      label: "admin (usuarios y contenido)",
      builtin: true,
      staffOtp: true,
      canManageUsers: true,
      modules: [...ROLE_MODULE_TEMPLATES.admin],
      systems: ["website"],
    },
    {
      id: "editor",
      label: "editor (contenido + CRM + quejas)",
      builtin: true,
      staffOtp: true,
      canManageUsers: false,
      modules: [...ROLE_MODULE_TEMPLATES.editor],
      systems: ["website"],
    },
    {
      id: "vendedor",
      label: "vendedor (clientes / leads)",
      builtin: true,
      staffOtp: false,
      canManageUsers: false,
      modules: [...ROLE_MODULE_TEMPLATES.vendedor],
      systems: ["website"],
    },
    {
      id: "calidad",
      label: "calidad (quejas LABSYSO)",
      builtin: true,
      staffOtp: false,
      canManageUsers: false,
      modules: [...ROLE_MODULE_TEMPLATES.calidad],
      systems: ["website"],
    },
    {
      id: "medico",
      label: "médico (SIGMO clínico)",
      builtin: true,
      staffOtp: false,
      canManageUsers: false,
      modules: [],
      systems: ["medicina"],
    },
  ];
}

export function slugRoleId(raw) {
  return String(raw || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 32);
}

export function isValidRoleId(id) {
  return /^[a-z][a-z0-9_]{1,31}$/.test(String(id || ""));
}

export function findCmsRole(catalog, roleId) {
  const id = String(roleId || "").trim();
  const list = Array.isArray(catalog) && catalog.length ? catalog : defaultCmsRoles();
  return list.find((r) => r.id === id) || null;
}

export function mergeCmsRoleCatalog(rawList) {
  const defaults = defaultCmsRoles();
  const incoming = Array.isArray(rawList) ? rawList : [];
  const byId = new Map();
  for (const row of incoming) {
    const id = slugRoleId(row?.id);
    if (!isValidRoleId(id)) continue;
    byId.set(id, row);
  }
  const out = defaults.map((base) => {
    const extra = byId.get(base.id);
    byId.delete(base.id);
    if (!extra) return { ...base };
    if (base.id === "owner") return { ...base };
    if (base.id === "medico") {
      return {
        ...base,
        label: String(extra.label || base.label).trim() || base.label,
      };
    }
    return {
      ...base,
      label: String(extra.label || base.label).trim() || base.label,
      staffOtp: typeof extra.staffOtp === "boolean" ? extra.staffOtp : base.staffOtp,
      modules: Array.isArray(extra.modules) ? sanitizeModules(extra.modules) : base.modules,
      systems: Array.isArray(extra.systems) && extra.systems.length ? extra.systems : base.systems,
    };
  });
  for (const row of byId.values()) {
    const id = slugRoleId(row.id);
    if (BUILTIN_ROLE_IDS.includes(id) || id === "owner") continue;
    out.push({
      id,
      label: String(row.label || id).trim() || id,
      builtin: false,
      staffOtp: row.staffOtp !== false,
      canManageUsers: false,
      modules: (Array.isArray(row.modules) ? row.modules : [...ROLE_MODULE_TEMPLATES.editor]).filter(
        (id) => id !== "users"
      ),
      systems: Array.isArray(row.systems) && row.systems.length ? row.systems : ["website"],
    });
  }
  return out;
}

export function roleLabel(catalog, roleId) {
  return findCmsRole(catalog, roleId)?.label || String(roleId || "");
}

/** Etiqueta segura para UI cuando owner debe permanecer oculto. */
export function roleLabelForUi(catalog, roleId, { showOwner = false } = {}) {
  const id = String(roleId || "").trim();
  if (id === "owner" && !showOwner) return id;
  const raw = roleLabel(catalog, id);
  if (id === "admin" && /sin ver owner/i.test(raw)) {
    return "admin (usuarios y contenido)";
  }
  return raw;
}

/** Opciones de rol para selects (owner oculto salvo sesión owner). */
export function rolesForSelect(catalog, { allowOwner = false, allowAdmin = true, currentValue } = {}) {
  const roles = mergeCmsRoleCatalog(catalog);
  const options = roles
    .filter((r) => {
      if (r.id === "owner") return allowOwner;
      if (r.id === "admin") return allowAdmin || currentValue === "admin";
      return true;
    })
    .map((r) => ({
      ...r,
      label: roleLabelForUi(catalog, r.id, { showOwner: allowOwner }),
    }));
  if (currentValue && !options.some((r) => r.id === currentValue)) {
    options.unshift({
      id: currentValue,
      label: roleLabelForUi(catalog, currentValue, { showOwner: allowOwner }),
    });
  }
  return options;
}

export function roleIsStaffOtp(catalog, roleId) {
  const r = String(roleId || "");
  if (r === "owner") return true;
  const def = findCmsRole(catalog, roleId);
  if (def) return Boolean(def.staffOtp);
  return r === "admin" || r === "editor";
}
